/**
 * Withdrawal engine (spec §7). Strategies live in the projection/solver; this
 * module sources a dollar need from accounts in order. Requirements honored:
 *
 *  - After-tax withdrawals pro-rate cost basis and generate realized LTCG.
 *    (Treating them as tax-free is "a common and serious error".)
 *  - Pre-59.5 withdrawals from pre-tax accounts incur the 10% penalty — EXCEPT
 *    457(b) after separation from service, and employer plans covered by the
 *    Rule of 55 (separation in or after the year you turn 55).
 *  - Roth withdrawals follow the statutory ordering rules and each conversion's
 *    own 5-year clock (see rothLayers.ts) — the conversion ladder.
 *  - Penalty-aware ordering (opt-out): accounts that would be penalized are
 *    held back until nothing penalty-free is left, rather than being drained in
 *    family order and eating a 10% loss with cash still sitting elsewhere.
 *  - RMDs are a floor: taken first from their accounts; spending above the RMD
 *    can come from anywhere in the order.
 *  - Bracket-aware mode (our addition): fill pre-tax withdrawals to the top of
 *    a target marginal bracket, take the remainder from after-tax, preserve
 *    Roth last — and only overflow back into pre-tax when nothing else is
 *    left, so the bracket ceiling bends rather than the plan failing.
 */
import type { Account, TaxFamily } from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import {
  AGE_59_5,
  drawFromRoth,
  emptyLayers,
  penaltyFreeAmount,
  type RothLayers,
} from './rothLayers';

export interface WithdrawalRequest {
  /** Amount of spending still unfunded after income. */
  need: number;
  /** Account-type order to draw from. */
  familyOrder: TaxFamily[];
  /** The calendar year — dates each Roth conversion's 5-year clock. */
  year: number;
  /** Owner ages this year, keyed by person id (for penalty logic). */
  agesByOwner: Record<string, number>;
  /** Whether each owner has separated from service (457(b) exception). */
  separatedByOwner: Record<string, boolean>;
  /**
   * Age each owner separates from service. The Rule of 55 turns on the age at
   * SEPARATION, not the age at withdrawal: leaving at 52 and waiting until 56
   * does not qualify, while leaving at 56 qualifies immediately.
   */
  separationAgeByOwner?: Record<string, number>;
  /** Roth layers (contributions / conversion vintages) per account id. */
  rothLayers?: Record<string, RothLayers>;
  /**
   * Hold penalized accounts back until every penalty-free source is exhausted.
   * Default ON — see Decisions.avoidEarlyPenalties.
   */
  avoidEarlyPenalties?: boolean;
  /**
   * Bracket-aware ordering: cap the FIRST pre-tax pass at this much ordinary
   * income (the room left below the target bracket top). Ignored when null.
   */
  pretaxRoomCap?: number;
  /**
   * Safety bucket: this account is EXCLUDED from the normal passes (protected
   * insurance), drawn as the very first pass when `drawFirst` is true (down
   * year / ACA year), and always available as the last-resort final pass so a
   * plan never fails while cash sits in the bucket.
   */
  bucket?: { accountId: string; drawFirst: boolean };
}

export interface AccountWithdrawal {
  accountId: string;
  amount: number;
  /** Ordinary-taxable portion (pre-tax accounts, HSA-after-65 nonmedical). */
  ordinaryIncome: number;
  /** Realized long-term gain (after-tax accounts, basis pro-rated). */
  realizedGain: number;
  /** Basis consumed (after-tax accounts). */
  basisConsumed: number;
  /** 10% early-withdrawal penalty incurred. */
  penalty: number;
  /** Roth accounts: layers left after this draw, for the caller to store. */
  rothLayersAfter?: RothLayers;
  /** Roth conversion dollars spent before their 5-year clock ran out. */
  unseasonedConversionsUsed?: number;
}

export interface WithdrawalPlanResult {
  withdrawals: AccountWithdrawal[];
  totalWithdrawn: number;
  totalOrdinaryIncome: number;
  totalRealizedGains: number;
  totalPenalties: number;
  unfundedNeed: number;
  /**
   * Spending that could only be funded by accepting a penalty. Zero in a
   * well-sequenced plan; when positive it names a real gap in the bridge.
   */
  penalizedWithdrawals: number;
}

export const TRADITIONAL_ORDER: TaxFamily[] = ['aftertax', 'pretax', 'roth', 'hsa'];

interface MutableAccountState {
  account: Account;
  balance: number;
  costBasis: number;
  layers: RothLayers;
}

/**
 * Whether a pre-tax withdrawal is penalized, after every exception the code
 * knows about: age, 457(b)-after-separation, inherited IRAs, and the Rule of 55.
 */
function pretaxPenaltyApplies(
  account: Account,
  ownerAge: number,
  separated: boolean,
  separationAge: number | undefined,
): boolean {
  const meta = ACCOUNT_META[account.type];
  if (!meta.earlyPenaltyApplies) return false; // 457(b), inherited IRAs
  if (ownerAge >= AGE_59_5) return false;
  if (meta.noPenaltyAfterSeparation && separated) return false;
  if (rule55Covers(account, ownerAge, separated, separationAge)) return false;
  return true;
}

/**
 * Rule of 55: separating from service in or after the year you turn 55 makes
 * THAT employer's plan penalty-free right away. Never applies to IRAs, and is
 * lost the moment the plan is rolled into one — so the account has to be
 * flagged as the plan being separated from.
 */
function rule55Covers(
  account: Account,
  ownerAge: number,
  separated: boolean,
  separationAge: number | undefined,
): boolean {
  return (
    ACCOUNT_META[account.type].rule55Possible === true &&
    account.currentEmployerPlan === true &&
    separated &&
    separationAge != null &&
    separationAge >= 55 &&
    ownerAge >= 55
  );
}

/** How much this account can supply with NO penalty at all right now. */
function penaltyFreeRoom(s: MutableAccountState, request: WithdrawalRequest): number {
  const meta = ACCOUNT_META[s.account.type];
  const ownerAge = request.agesByOwner[s.account.ownerId] ?? 99;
  const separated = request.separatedByOwner[s.account.ownerId] ?? true;
  const separationAge = request.separationAgeByOwner?.[s.account.ownerId];

  switch (meta.taxFamily) {
    case 'pretax':
      return pretaxPenaltyApplies(s.account, ownerAge, separated, separationAge) ? 0 : s.balance;
    case 'roth':
      return penaltyFreeAmount(
        s.layers,
        s.balance,
        request.year,
        ownerAge,
        rothPenaltyExempt(s.account, ownerAge, separated, separationAge),
      );
    case 'hsa':
      return ownerAge >= 65 ? s.balance : 0;
    default:
      return s.balance; // after-tax and 529 carry no early-withdrawal penalty
  }
}

/** Roth money can also escape the penalty via 457(b) separation or Rule of 55. */
function rothPenaltyExempt(
  account: Account,
  ownerAge: number,
  separated: boolean,
  separationAge: number | undefined,
): boolean {
  const meta = ACCOUNT_META[account.type];
  if (!meta.earlyPenaltyApplies) return true; // Roth 457(b), inherited Roth
  if (meta.noPenaltyAfterSeparation && separated) return true;
  return rule55Covers(account, ownerAge, separated, separationAge);
}

/**
 * Withdraw `request.need` from `accounts` (mutating the passed balances map is
 * the caller's job — this function is pure and returns the flows).
 *
 * @param balances current balance per account id
 * @param bases    current cost basis per account id (after-tax only)
 */
export function planWithdrawals(
  accounts: Account[],
  balances: Record<string, number>,
  bases: Record<string, number>,
  request: WithdrawalRequest,
): WithdrawalPlanResult {
  const states: MutableAccountState[] = accounts.map((a) => ({
    account: a,
    balance: balances[a.id] ?? 0,
    costBasis: bases[a.id] ?? a.costBasis ?? 0,
    layers: request.rothLayers?.[a.id] ?? emptyLayers(),
  }));

  const withdrawals: AccountWithdrawal[] = [];
  let remaining = Math.max(0, request.need);
  let penalizedWithdrawals = 0;
  const bucketId = request.bucket?.accountId;
  const avoidPenalties = request.avoidEarlyPenalties ?? true;

  /** Draw from one account, recording the flow and updating its state.
   *  Returns the amount actually taken. */
  const take = (s: MutableAccountState, amount: number): number => {
    if (amount <= 0) return 0;
    const w = withdrawFrom(s, amount, request);
    withdrawals.push(w);
    s.balance -= amount;
    s.costBasis = Math.max(0, s.costBasis - w.basisConsumed);
    if (w.rothLayersAfter) s.layers = w.rothLayersAfter;
    remaining -= amount;
    if (w.penalty > 0) penalizedWithdrawals += amount;
    return amount;
  };

  const drainBucket = () => {
    if (!bucketId || remaining <= 0) return;
    const s = states.find((x) => x.account.id === bucketId && x.balance > 0);
    if (!s) return;
    take(s, Math.min(remaining, s.balance));
  };

  // Down-market / ACA-protection years: the bucket funds spending first.
  if (request.bucket?.drawFirst) drainBucket();

  // Each pass drains one tax family, optionally capped. Bracket-aware mode
  // prepends a capped pre-tax pass and appends an uncapped fallback one.
  const passes: { family: TaxFamily; cap: number }[] =
    request.pretaxRoomCap != null
      ? [
          { family: 'pretax', cap: Math.max(0, request.pretaxRoomCap) },
          { family: 'aftertax', cap: Infinity },
          { family: 'roth', cap: Infinity },
          { family: 'hsa', cap: Infinity },
          { family: 'pretax', cap: Infinity }, // last resort past the bracket
        ]
      : request.familyOrder.map((family) => ({ family, cap: Infinity }));

  for (const pass of passes) {
    if (remaining <= 0) break;
    let passRoom = pass.cap;
    // 529s are kept out of the retirement pool by default (spec §4); the
    // safety bucket is protected from normal passes.
    const pool = states.filter(
      (s) =>
        ACCOUNT_META[s.account.type].taxFamily === pass.family &&
        s.balance > 0 &&
        s.account.id !== bucketId,
    );
    for (const s of pool) {
      if (remaining <= 0 || passRoom <= 0) break;
      // Penalty-aware: a normal pass may only take what comes out clean. A
      // Roth account can be partly available (contributions and seasoned
      // conversions) and partly not — hence a room cap, not a skip.
      const ceiling = avoidPenalties
        ? Math.min(s.balance, penaltyFreeRoom(s, request))
        : s.balance;
      passRoom -= take(s, Math.min(remaining, passRoom, ceiling));
    }
  }

  // Last resort: a plan never fails while cash sits in the bucket. Penalty-free
  // bucket cash is spent BEFORE accepting a 10% penalty — that ordering is the
  // whole point of holding it.
  drainBucket();

  // Only now, with every clean source exhausted, take the penalty. Skipping
  // this would report a plan as failed while the money was sitting right there.
  if (avoidPenalties && remaining > 0) {
    for (const family of request.familyOrder) {
      if (remaining <= 0) break;
      for (const s of states) {
        if (remaining <= 0) break;
        if (ACCOUNT_META[s.account.type].taxFamily !== family) continue;
        if (s.balance <= 0) continue;
        take(s, Math.min(remaining, s.balance));
      }
    }
  }

  return {
    withdrawals,
    totalWithdrawn: withdrawals.reduce((t, w) => t + w.amount, 0),
    totalOrdinaryIncome: withdrawals.reduce((t, w) => t + w.ordinaryIncome, 0),
    totalRealizedGains: withdrawals.reduce((t, w) => t + w.realizedGain, 0),
    totalPenalties: withdrawals.reduce((t, w) => t + w.penalty, 0),
    unfundedNeed: remaining,
    penalizedWithdrawals,
  };
}

function withdrawFrom(
  s: MutableAccountState,
  amount: number,
  request: WithdrawalRequest,
): AccountWithdrawal {
  const meta = ACCOUNT_META[s.account.type];
  const ownerAge = request.agesByOwner[s.account.ownerId] ?? 99;
  const separated = request.separatedByOwner[s.account.ownerId] ?? true;
  const separationAge = request.separationAgeByOwner?.[s.account.ownerId];

  let ordinaryIncome = 0;
  let realizedGain = 0;
  let basisConsumed = 0;
  let penalty = 0;
  let rothLayersAfter: RothLayers | undefined;
  let unseasonedConversionsUsed: number | undefined;

  switch (meta.taxFamily) {
    case 'pretax': {
      ordinaryIncome = amount;
      if (pretaxPenaltyApplies(s.account, ownerAge, separated, separationAge)) {
        penalty = amount * 0.1;
      }
      break;
    }
    case 'aftertax': {
      // Pro-rate basis: gain fraction = (balance - basis) / balance.
      const gainFraction = s.balance > 0 ? Math.max(0, (s.balance - s.costBasis) / s.balance) : 0;
      realizedGain = amount * gainFraction;
      basisConsumed = amount - realizedGain;
      break;
    }
    case 'roth': {
      // Statutory ordering: contributions, then conversions oldest-first with
      // their own 5-year clocks, then earnings (see rothLayers.ts).
      const draw = drawFromRoth(
        s.layers,
        s.balance,
        amount,
        request.year,
        ownerAge,
        rothPenaltyExempt(s.account, ownerAge, separated, separationAge),
      );
      ordinaryIncome = draw.ordinaryIncome;
      penalty = draw.penalty;
      rothLayersAfter = draw.remaining;
      unseasonedConversionsUsed = draw.unseasonedConversionsUsed;
      break;
    }
    case 'hsa': {
      // Modeled as medical-expense-first elsewhere; a general withdrawal here
      // after 65 is ordinary income, before 65 penalized + ordinary.
      if (ownerAge >= 65) {
        ordinaryIncome = amount;
      } else {
        ordinaryIncome = amount;
        penalty = amount * 0.2; // HSA non-qualified penalty is 20%
      }
      break;
    }
    case 'education':
      // 529 stays out of the retirement pool; if explicitly drawn, treat
      // earnings as ordinary + 10% penalty in later phases. Foundation: no-op.
      break;
  }

  return {
    accountId: s.account.id,
    amount,
    ordinaryIncome,
    realizedGain,
    basisConsumed,
    penalty,
    rothLayersAfter,
    unseasonedConversionsUsed,
  };
}
